// main Page - Purchase with Supplier details and invoice details
import React, { useEffect, useRef, useState } from "react";
import { IndianStates } from "../../../../../Data/Customer/api";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Modal,
  ModalBody,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getbranchData } from "../../../../../redux/authentication";
import { getInventorySupplier } from "../../../../../redux/inventory";
import PurchaseEntry from "./Bills/PurchaseEntry";

const STORAGE_KEY_PURCHASE_FORM = "purchase_form_persistence_data";

const formatDate = (dte) => {
  const now = new Date(dte);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(now.getTime() + istOffset);
  const formattedDate = istTime.toISOString().split("T")[0];
  return formattedDate;
};

const PurchaseForm = ({
  editFormData,
  togglePurchaseEntry,
  mode,
}) => {
  const itemsList = useSelector((state) => state.itemsList);
  const auth = useSelector((state) => state.authentication);
  const dispatch = useDispatch();
  const { userData } = auth;
  const { supplierData, purchaseData } = itemsList;
  const [error, setError] = useState("");
  const [errorDate, setErrorDate] = useState("");
  const [showModal, setShowModal] = useState(false);

  const registrationType = [
    { value: "", label: "Select Registration Type" },
    { value: "consumer", label: "Consumer" },
    { value: "regular", label: "Regular" },
    { value: "composition", label: "Composition" },
    { value: "unregistered", label: "Unregistered" },
  ];

  const defaultFormObj = {
    supplier: {
      supplierId: "",
      address: "",
      state: "",
      contactNo: "",
      partyName: "",
    },
    invoice: "",
    paymentDetails: {
      invoice_id: "",
      invoice_date: "",
      payment_mode: "",
      amount_paid: "",
      GSTIN: "",
      registrationType: "",
    },
    additionalDetails: {
      notes: "",
      returnReason: "",
      credit: "",
      returnDate: "",
    },
    createdBy: userData?.user || "",
  };

  // 1. Safe Initialization with LocalStorage fallback ensuring nested objects exist
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PURCHASE_FORM);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaultFormObj to ensure no nested keys are missing after updates
        return {
          ...defaultFormObj,
          ...parsed,
          supplier: { ...defaultFormObj.supplier, ...(parsed.supplier || {}) },
          paymentDetails: { ...defaultFormObj.paymentDetails, ...(parsed.paymentDetails || {}) },
          additionalDetails: { ...defaultFormObj.additionalDetails, ...(parsed.additionalDetails || {}) },
        };
      }
    } catch (e) {
      console.error("Error loading from localStorage", e);
    }
    return defaultFormObj;
  });

  // 2. Immediate storage sync helper
  const saveToLocalStorage = (updatedData) => {
    try {
      localStorage.setItem(STORAGE_KEY_PURCHASE_FORM, JSON.stringify(updatedData));
    } catch (error) {
      console.error("Failed to persist purchase form data:", error);
    }
  };

  // 3. Beforeunload unmount safeguard
  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.setItem(STORAGE_KEY_PURCHASE_FORM, JSON.stringify(formData));
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [formData]);

  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedRtnDate, setSelectedRtnDate] = useState(formatDate(new Date()));

  const validateDate = (dateString) => {
    if (typeof dateString !== "string") return false;
    const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = dateString.match(regex);
    if (!match) return false;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);

    const date = new Date(year, month, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    );
  };

  const clearForm = () => {
    localStorage.removeItem(STORAGE_KEY_PURCHASE_FORM);
    setFormData(defaultFormObj);
  };

  function toTitleCase(text) {
    if (!text) return "";
    return text.replace(/\w\S*/g, function (word) {
      return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
    });
  }

  // Handle Supplier Selection & Autofill safely
  useEffect(() => {
    if (supplierData?.length && formData?.supplier?.supplierId) {
      const supplier = supplierData.find(
        (item) => item.supplierId === formData.supplier.supplierId
      );

      if (supplier) {
        setFormData((prevFormData) => {
          const updated = {
            ...prevFormData,
            supplier: {
              ...prevFormData.supplier,
              address: prevFormData.supplier.address || supplier.supplierAddress,
              GSTIN: prevFormData.supplier.GSTIN || supplier.GSTIN,
              contactNo: prevFormData.supplier.contactNo || supplier.supplierContact,
              state: prevFormData.supplier.state || toTitleCase(supplier.state),
              partyName: prevFormData.supplier.partyName || supplier.supplierName,
            },
          };
          saveToLocalStorage(updated);
          return updated;
        });
      }
    }
  }, [supplierData, formData?.supplier?.supplierId]);

  useEffect(() => {
    if (validateDate(selectedDate)) {
      const nDate = new Date(selectedDate);
      const withoutHours = new Date(nDate.setHours(0, 0, 0, 0)).toISOString();
      setFormData((prev) => {
        const updated = {
          ...prev,
          paymentDetails: {
            ...prev.paymentDetails,
            invoice_date: withoutHours,
          },
        };
        saveToLocalStorage(updated);
        return updated;
      });
    }
  }, [selectedDate]);

  useEffect(() => {
    if (
      validateDate(selectedRtnDate) &&
      formData.additionalDetails?.returnReason
    ) {
      const nDate = new Date(selectedRtnDate);
      const withoutHours = new Date(nDate.setHours(0, 0, 0, 0)).toISOString();
      setFormData((prev) => {
        const updated = {
          ...prev,
          additionalDetails: {
            ...prev.additionalDetails,
            returnDate: withoutHours,
          },
        };
        saveToLocalStorage(updated);
        return updated;
      });
    }
  }, [selectedRtnDate, formData.additionalDetails?.returnReason]);

  useEffect(() => {
    if (mode === "Return Mode") {
      setSelectedRtnDate(formatDate(new Date()));
    }
  }, [mode]);

  useEffect(() => {
    if (editFormData) {
      setFormData(editFormData);
      saveToLocalStorage(editFormData);
      if (editFormData.paymentDetails?.invoice_date) {
        setSelectedDate(() =>
          formatDate(editFormData.paymentDetails.invoice_date)
        );
      }
    }
  }, [editFormData]);

  useEffect(() => {
    if (userData?.token) {
      dispatch(
        getbranchData({ token: userData.token, branch: userData.branch })
      );
      dispatch(
        getInventorySupplier({ token: userData.token, branch: userData.branch })
      );
    }
  }, [userData?.token]);

  return (
    <Container fluid>
      <Card className="mb-5 mt-2">
        <CardHeader>
          <Row className="d-flex justify-content-center">
            <Col md={3} style={{ textAlign: "center" }}>
              <h4>
                {mode === "Return Mode" ? "Debit Entry" : "Purchase Entry"}
              </h4>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <Form>
            <Row>
              <Col md={5}>
                <FormGroup>
                  <Label for="supplier">Supplier</Label>
                  <Input
                    type="select"
                    name="supplierId"
                    id="supplier"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.supplierId || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedSupplier = supplierData?.find(
                        (item) => item.supplierId === selectedId
                      );

                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            supplierId: selectedId,
                            address: selectedSupplier ? selectedSupplier.supplierAddress : "",
                            GSTIN: selectedSupplier ? selectedSupplier.GSTIN : "",
                            contactNo: selectedSupplier ? selectedSupplier.supplierContact : "",
                            state: selectedSupplier ? toTitleCase(selectedSupplier.state) : "",
                            partyName: selectedSupplier ? selectedSupplier.supplierName : "",
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                    placeholder="Enter supplier"
                  >
                    <option value="">Select supplier</option>
                    {Array.isArray(supplierData) &&
                      supplierData.map((item, idx) => (
                        <option key={idx} value={item.supplierId}>
                          {item.supplierName}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="invoice">Invoice No</Label>
                  <Input
                    type="text"
                    name="invoice_id"
                    id="invoice"
                    disabled={mode === "Return Mode"}
                    value={formData.paymentDetails?.invoice_id || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          paymentDetails: {
                            ...prev.paymentDetails,
                            invoice_id: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                    placeholder="Enter invoice No."
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="invoice-date">Invoice Date</Label>
                  <Input
                    type="date"
                    name="invoice_date"
                    id="invoice-date"
                    disabled={mode === "Return Mode"}
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                    }}
                    placeholder="Enter invoice Date"
                  />
                  {errorDate && (
                    <span style={{ color: "red" }}>{errorDate}</span>
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <FormGroup>
                  <Label for="party">Party Name</Label>
                  <Input
                    type="text"
                    name="partyName"
                    disabled={mode === "Return Mode"}
                    id="party"
                    value={formData.supplier?.partyName || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            partyName: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                    placeholder="Enter party name"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="taxInput">GSTIN</Label>
                  <Input
                    type="text"
                    name="GSTIN"
                    id="taxInput"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.GSTIN || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            GSTIN: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                  />
                </FormGroup>
              </Col>
              <Col md={5}>
                <FormGroup>
                  <Label for="tax">Address</Label>
                  <Input
                    type="textarea"
                    name="address"
                    disabled={mode === "Return Mode"}
                    id="tax"
                    value={formData.supplier?.address || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            address: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                    placeholder="Enter Address"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="batch">State</Label>
                  <Input
                    type="select"
                    name="state"
                    id="state"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.state || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            state: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                  >
                    <option value="">Select State</option>
                    {IndianStates.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label for="contact">Contact No</Label>
                  <Input
                    type="text"
                    name="contactNo"
                    id="contact"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.contactNo || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          supplier: {
                            ...prev.supplier,
                            contactNo: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                    placeholder="Enter contact No."
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="hsn"> GST Registration Type</Label>
                  <Input
                    type="select"
                    name="registrationType"
                    id="hsn"
                    disabled={mode === "Return Mode"}
                    value={formData.paymentDetails?.registrationType || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          paymentDetails: {
                            ...prev.paymentDetails,
                            registrationType: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                  >
                    {registrationType.map((option, id) => (
                      <option key={id} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col md="3">
                <Label>Amount Paid</Label>
                <Input
                  value={formData.paymentDetails?.amount_paid || ""}
                  disabled={mode === "Return Mode"}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isNaN(value)) {
                      setError("Please enter a valid number");
                    } else {
                      setError("");
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          paymentDetails: {
                            ...prev.paymentDetails,
                            amount_paid: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }
                  }}
                />
                {error && <span style={{ color: "red" }}>{error}</span>}
              </Col>
              <Col md="3">
                <Label>Payment Mode</Label>
                <Input
                  id="paymode"
                  type="text"
                  list="paymentmode"
                  name="input1"
                  disabled={mode === "Return Mode"}
                  value={formData.paymentDetails?.payment_mode || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => {
                      const updated = {
                        ...prev,
                        paymentDetails: {
                          ...prev.paymentDetails,
                          payment_mode: value,
                        },
                      };
                      saveToLocalStorage(updated);
                      return updated;
                    });
                  }}
                />
                <datalist id="paymentmode">
                  <option value="UPI" />
                  <option value="Cash" />
                  <option value="Credit Card" />
                  <option value="Debit Card" />
                  <option value="Cheque" />
                  <option value="Net Banking" />
                </datalist>
              </Col>
              {mode === "Return Mode" && (
                <Col md="2">
                  <Label>Credit Amount</Label>
                  <Input
                    id="credit"
                    type="text"
                    name="credit"
                    value={formData.additionalDetails?.credit || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => {
                        const updated = {
                          ...prev,
                          additionalDetails: {
                            ...prev.additionalDetails,
                            credit: value,
                          },
                        };
                        saveToLocalStorage(updated);
                        return updated;
                      });
                    }}
                  />
                </Col>
              )}
              {mode === "Return Mode" && (
                <>
                  <Col md="2">
                    <Label>Return Reason</Label>
                    <Input
                      id="return"
                      type="text"
                      name="returnReason"
                      value={formData.additionalDetails?.returnReason || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData((prev) => {
                          const updated = {
                            ...prev,
                            additionalDetails: {
                              ...prev.additionalDetails,
                              returnReason: value,
                            },
                          };
                          saveToLocalStorage(updated);
                          return updated;
                        });
                      }}
                    />
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="invoice-date">Return Date</Label>
                      <Input
                        type="date"
                        name="invoice_rtn_date"
                        id="nvoice_rtn_date"
                        value={selectedRtnDate}
                        onChange={(e) => {
                          setSelectedRtnDate(e.target.value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
            </Row>
          </Form>

          <PurchaseEntry
            purchaseInvoiceFormData={formData}
            selectedRtnDate={selectedRtnDate}
            mode={mode}
            clearSupplierForm={clearForm}
            setShowModal={setShowModal}
            togglePurchaseEntry={togglePurchaseEntry}
          />
        </CardBody>
      </Card>
      <Modal isOpen={showModal} toggle={setShowModal} centered>
        <ModalBody style={{ textAlign: "center", padding: "20px" }}>
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p
            style={{ marginTop: "10px", fontSize: "16px", fontWeight: "bold" }}
          >
            Saving...
          </p>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PurchaseForm;